import { useAxios } from "../../../hooks/useAxios"
import { useCallback, useMemo, useRef, useState } from "react"
import { FilterService, Panel, Service, ViewTriggerAutomatedServiceMap } from "../types"
import { toast } from "react-toastify";
import { toastError, toastSuccess } from "../../assets/customToasts";
import { globalAxios } from "../../../services/globalAxios";
import useDelete from "../../../hooks/useDelete";
import usePostPut from "../../../hooks/usePostPut";

const API_URL = process.env.REACT_APP_NOTIFIER_URL

export function useServicesService() {

    const {
        data,
        isLoading,
        error,
        fetchData: refetchServices,
    } = useAxios<ViewTriggerAutomatedServiceMap[]>(`${API_URL}/automated-services`)

    const originalTriggers = useRef<number[]>([])

    const [panel, setPanel] = useState<Panel>({
        show: false
    })

    const [modal, setModal] = useState<Panel>({
        show: false
    })

    const [filters, setFilters] = useState<FilterService>({
        trigger: 0,
        automated_service_name: '',
    })

    const filterByName = useCallback((row: ViewTriggerAutomatedServiceMap) => {
        const value = filters.automated_service_name.toLowerCase()
        if (!value) return true

        return row.automated_service_name.toLowerCase().includes(value)
    }, [filters.automated_service_name])

    const filterByTrigger = useCallback((row: ViewTriggerAutomatedServiceMap) => {
        const value = filters.trigger
        const triggers = row.triggers.map(item => item.id)

        if (value === 0 || triggers.includes(value)) return true

        return false
    }, [filters.trigger])

    const filteredServices = useMemo(() => {
        if (!data) return []

        return data
            .filter(filterByName)
            .filter(filterByTrigger)
    }, [data, filterByName, filterByTrigger])

    const handleClearFilters = useCallback(() => {
        setFilters({ trigger: 0, automated_service_name: '' })
    }, [])

    const { handleUpdateOrCreate, isHandlingUpdateOrCreate } = usePostPut<Service>({
        onSettled: () => setPanel({ show: false }),
        onSuccess: () => refetchServices()
    })

    const { handleDelete, isHandlingDelete } = useDelete({
        onSettled: () => setModal({ show: false }),
        onSuccess: () => refetchServices()
    })

    const preparePayload = (formData: Service, existingId?: number) => {
        const { automated_service_name, path, triggers } = formData
        const triggerIds = triggers.map(item => item.id)

        const payload: any = {
            automated_service_name,
            path
        }

        if (!existingId) return {
            ...payload,
            triggers: triggerIds
        }

        return {
            ...payload,
            id: existingId,
            new_triggers: triggerIds.filter((id) => !originalTriggers.current.includes(id)),
            deleted_triggers: originalTriggers.current.filter((id: number) => !triggerIds.includes(id))
        }
    }

    const handleRunService = async (id: number): Promise<void> => {
        const _toast = toast.loading('Running service...', { toastId: 'toast-service-run' })

        const path = data?.find((item) => item.fk_id_automated_service === id)?.path

        try {
            await globalAxios.get(`${path}`)
            toastSuccess(_toast, 'Service run.')
        } catch (error) {
            console.log(error)
            toastError(_toast, 'Could not run service')
        }

    }

    return {
        // Data
        data: data || [],
        filteredServices,
        filters,
        originalTriggers,

        // States
        isLoading,
        error,
        isHandlingUpdateOrCreate,
        isHandlingDelete,

        // Panel
        panel,
        setPanel,

        // Modal 
        modal,
        setModal,

        // Setters & Handlers
        setFilters,
        handleUpdateOrCreate,
        handleDelete,
        handleClearFilters,
        handleRunService,
        preparePayload
    }
}
